import { useMemo } from 'react';
import { type InferFilterItemFromFilter, type OperatorType, parseFilterItems } from '@adv-frontend/filters';
import { type OpenApiSchemaCharacteristicMeta } from '#shared/api/schema';
import { SEARCH_PARAM_KEY_PAGE } from '#shared/components/grid';
import { convertDynamicFiltersForBackend } from '#shared/lib/dynamicFilters';
import { useGridSearchParams, usePropertyFromSearchParams } from '#shared/lib/hooks';
import { PRODUCT_FILTER_SEARCH_PARAM_KEYS } from '../constants';
import { type ProductFilterDeclaration, useProductFiltersDeclaration } from './useProductFiltersDeclaration';

type FilterOperator = { operator: keyof typeof OperatorType };

type FilterArray = {
    field: string;
    values: unknown[];
} & FilterOperator;

type FilterSingle = {
    field: string;
    value: unknown;
} & FilterOperator;

type Filter = FilterArray | FilterSingle;

/*
 * Собирает массив фильтров в специальном формате для отправки на бекенд
 */
type UseProductFilterForBackendRequestOptions = {
    skipUrlDynamicFilters?: boolean;
};

export const useProductFilterForBackendRequest = (
    characteristics: OpenApiSchemaCharacteristicMeta[] | undefined,
    forceFilterItems: InferFilterItemFromFilter<ProductFilterDeclaration>[] = [],
    { skipUrlDynamicFilters = false }: UseProductFilterForBackendRequestOptions = {}
): Filter[] => {
    const { searchParams, setSearchParams } = useGridSearchParams();
    const { searchParam: filter } = usePropertyFromSearchParams(
        searchParams,
        setSearchParams,
        PRODUCT_FILTER_SEARCH_PARAM_KEYS.filter,
        [SEARCH_PARAM_KEY_PAGE]
    );
    const filtersDeclaration = useProductFiltersDeclaration(characteristics);

    const parsedFilterItems = useMemo(
        () => (skipUrlDynamicFilters ? [] : parseFilterItems(filtersDeclaration, filter)),
        [filtersDeclaration, filter, skipUrlDynamicFilters]
    );

    return useMemo(
        () => convertDynamicFiltersForBackend([...parsedFilterItems, ...forceFilterItems]),
        [forceFilterItems, parsedFilterItems]
    );
};
